import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { SearchSuggestion } from '../../models/search-suggestion.model';
import { QuickResult } from '../../models/quick-result.model';
import { normalizeText, tokenizeText } from '../../utils/text-normalize.util';
import { CapitalizePipe } from '../../pipes/capitalize.pipe';

@Component({
  selector: 'app-search-bar',
  standalone: true,
  imports: [CommonModule, CapitalizePipe],
  templateUrl: './search-bar.component.html'
})
export class SearchBarComponent implements OnChanges, OnDestroy {
  @Input() query = '';
  @Input() placeholder = '';
  @Input() clearLabel = '';
  @Input() countyLabel = '';
  @Input() cityLabel = '';
  @Input() suggestions: SearchSuggestion[] = [];
  @Input() useResultDropdown = false;
  @Input() resultItems: QuickResult[] = [];

  @Output() queryChange = new EventEmitter<string>();
  @Output() clear = new EventEmitter<void>();
  @Output() resultSelected = new EventEmitter<QuickResult>();
  @Output() searchSubmit = new EventEmitter<void>();

  @ViewChild('searchInput', { static: true }) searchInput?: ElementRef<HTMLInputElement>;
  @ViewChildren('suggestionItem') suggestionButtons?: QueryList<ElementRef<HTMLButtonElement>>;

  inputValue = '';
  filteredSuggestions: SearchSuggestion[] = [];
  filteredResults: QuickResult[] = [];
  showSuggestions = false;
  highlightedIndex = -1;
  dropdownLeft = 0;
  dropdownTop = 0;
  dropdownWidth = 0;

  private blurTimeoutId: number | null = null;
  private inputDebounceId: number | null = null;
  private readonly onDocumentClick = (event: MouseEvent) => this.handleDocumentClick(event);

  ngOnDestroy(): void {
    document.removeEventListener('mousedown', this.onDocumentClick);
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onWindowChange(): void {
    if (this.showSuggestions) {
      this.updateDropdownPosition();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['query']) {
      this.inputValue = this.query ?? '';
      if (!this.useResultDropdown) {
        this.updateFilteredSuggestions();
      }
    }
    if (!this.useResultDropdown && changes['suggestions'] && !changes['suggestions'].firstChange) {
      this.updateFilteredSuggestions();
    }
    if (this.useResultDropdown && changes['resultItems']) {
      this.applyResultItems();
    }
    if (changes['useResultDropdown']) {
      if (this.useResultDropdown) {
        this.applyResultItems();
      } else {
        this.hideSuggestions();
        this.filteredResults = [];
      }
    }
  }

  onInputChange(value: string): void {
    console.log('[DEBUG] onInputChange triggered with value:', value);
    this.inputValue = value;
    this.queryChange.emit(value);

    if (this.inputDebounceId !== null) {
      window.clearTimeout(this.inputDebounceId);
    }

    if (this.useResultDropdown) {
      this.showSuggestions = false;
      this.highlightedIndex = -1;
    } else {
      this.inputDebounceId = window.setTimeout(() => {
        console.log('[DEBUG] Showing suggestions after debounce');
        this.updateFilteredSuggestions();
        this.inputDebounceId = null;
      }, 300);
    }
  }

  onClear(): void {
    this.inputValue = '';
    this.clear.emit();
    this.hideSuggestions();
  }

  onInputFocus(): void {
    if (this.blurTimeoutId !== null) {
      window.clearTimeout(this.blurTimeoutId);
      this.blurTimeoutId = null;
    }
    // Do not open suggestions on focus, only on typing
  }

  onInputBlur(): void {
    this.blurTimeoutId = window.setTimeout(() => {
      this.hideSuggestions();
      this.blurTimeoutId = null;
    }, 200);
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.hideSuggestions();
      return;
    }
    const listLength = this.useResultDropdown ? this.filteredResults.length : this.filteredSuggestions.length;
    const hasItems = listLength > 0;

    if (event.key === 'ArrowDown') {
      if (hasItems) {
        event.preventDefault();
        this.moveHighlight(1);
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      if (hasItems) {
        event.preventDefault();
        this.moveHighlight(-1);
      }
      return;
    }

    if (event.key === 'Enter') {
      // Always hide suggestions when pressing Enter
      if (this.useResultDropdown) {
        event.preventDefault();

        if (hasItems && this.highlightedIndex >= 0 && this.highlightedIndex < listLength) {
          const quickResult = this.filteredResults[this.highlightedIndex];
          this.selectResult(quickResult);
          return;
        }

        this.hideSuggestions();
        this.searchSubmit.emit();
        return;
      }

      this.hideSuggestions();

      if (hasItems && this.highlightedIndex >= 0 && this.highlightedIndex < listLength) {
        event.preventDefault();
        const suggestion = this.filteredSuggestions[this.highlightedIndex];
        this.applySuggestion(suggestion);
        return;
      }

      this.searchSubmit.emit();
    }
  }

  onSuggestionMouseDown(event: MouseEvent, suggestion: SearchSuggestion): void {
    event.preventDefault(); // Prevent input blur
    if (this.blurTimeoutId !== null) {
      window.clearTimeout(this.blurTimeoutId);
      this.blurTimeoutId = null;
    }
    this.applySuggestion(suggestion);
  }

  trackSuggestion(_index: number, item: SearchSuggestion): string {
    return `${item.type}-${item.id}`;
  }

  onResultMouseDown(event: MouseEvent, result: QuickResult): void {
    event.preventDefault(); // Prevent input blur
    if (this.blurTimeoutId !== null) {
      window.clearTimeout(this.blurTimeoutId);
      this.blurTimeoutId = null;
    }
    this.selectResult(result);
  }

  private applySuggestion(suggestion: SearchSuggestion): void {
    const trailingSpacesMatch = this.inputValue.match(/\s+$/);
    const trailingSpacesLength = trailingSpacesMatch ? trailingSpacesMatch[0].length : 0;
    const baseValue = this.inputValue.slice(0, this.inputValue.length - trailingSpacesLength);
    const tokenMatch = baseValue.match(/([^,;:/\\|\s]+)$/);
    const prefix = tokenMatch && tokenMatch.index !== undefined ? baseValue.slice(0, tokenMatch.index) : baseValue;
    const separator = prefix.endsWith(' ') || !prefix.length ? '' : ' ';
    // Append a space after the suggestion to signal completion of the word
    const newValue = `${prefix}${separator}${suggestion.label} `.trimStart();

    this.inputValue = newValue;
    this.queryChange.emit(newValue);
    this.hideSuggestions();
    this.highlightedIndex = -1;

    requestAnimationFrame(() => {
      const element = this.searchInput?.nativeElement;
      if (!element) {
        return;
      }
      element.value = newValue;
      element.focus();
      const position = newValue.length;
      element.setSelectionRange(position, position);
    });
  }

  private calculateTextWidth(text: string): number {
    const inputEl = this.searchInput?.nativeElement;
    if (!inputEl) {
      return 0;
    }

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      return 0;
    }

    const style = window.getComputedStyle(inputEl);
    context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    
    // We also need the left padding of the input to align correctly
    const paddingLeft = parseFloat(style.paddingLeft) || 0;
    
    return context.measureText(text).width + paddingLeft;
  }

  private updateDropdownPosition(): void {
    const inputEl = this.searchInput?.nativeElement;
    if (!inputEl) {
      return;
    }
    const rect = inputEl.getBoundingClientRect();
    this.dropdownTop = rect.bottom + 4;
    this.dropdownWidth = this.useResultDropdown ? rect.width : Math.min(480, rect.width);

    if (this.useResultDropdown) {
      this.dropdownLeft = rect.left;
      return;
    }

    // Calculate left: input's viewport X + text width before current token
    const activeToken = this.extractActiveToken(this.inputValue);
    if (activeToken) {
      const prefixLength = this.inputValue.lastIndexOf(activeToken);
      const prefixText = this.inputValue.substring(0, prefixLength);
      this.dropdownLeft = rect.left + this.calculateTextWidth(prefixText);
    } else {
      this.dropdownLeft = rect.left;
    }
  }

  private handleDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    const searchBarEl = this.searchInput?.nativeElement?.closest('.search-bar');
    if (searchBarEl && !searchBarEl.contains(target)) {
      this.hideSuggestions();
    }
  }

  private updateFilteredSuggestions(): void {
    if (this.useResultDropdown) {
      return;
    }
    const activeToken = this.extractActiveToken(this.inputValue);
    const normalizedToken = normalizeText(activeToken);

    if (normalizedToken.length < 2 || !this.suggestions.length) {
      this.hideSuggestions();
      return;
    }

    this.updateDropdownPosition();

    const matches = this.suggestions
      .filter(suggestion => this.matchesSuggestion(suggestion, normalizedToken))
      .slice(0, 10);

    this.filteredSuggestions = matches;
    if (!matches.length) {
      this.hideSuggestions();
      return;
    }

    // Only show if focused
    const isFocused = this.searchInput?.nativeElement === document.activeElement;
    if (!isFocused) {
      console.log('[DEBUG] NOT showing suggestions via updateFilteredSuggestions - Input is NOT focused');
      return;
    }

    // Don't preselect any item - user must use arrow keys to select
    this.highlightedIndex = -1;

    console.log('[DEBUG] Showing suggestions via updateFilteredSuggestions');
    this.showSuggestions = true;
    this.updateDropdownPosition();
    document.addEventListener('mousedown', this.onDocumentClick);
    this.ensureHighlightedVisible();
  }

  private matchesSuggestion(suggestion: SearchSuggestion, normalizedToken: string): boolean {
    if (!normalizedToken) {
      return false;
    }

    const tokens = tokenizeText(suggestion.label);
    return tokens.some(token => token.startsWith(normalizedToken));
  }

  private extractActiveToken(value: string): string {
    if (/\s$/.test(value)) {
      return '';
    }
    const withoutTrailingSpaces = value.replace(/\s+$/, '');
    const match = withoutTrailingSpaces.match(/([^,;:/\\|\s]+)$/);
    return match ? match[1] : '';
  }

  private hideSuggestions(): void {
    if (this.blurTimeoutId !== null) {
      window.clearTimeout(this.blurTimeoutId);
      this.blurTimeoutId = null;
    }
    if (!this.useResultDropdown) {
      this.filteredSuggestions = [];
    }
    this.showSuggestions = false;
    this.highlightedIndex = -1;
    document.removeEventListener('mousedown', this.onDocumentClick);
  }

  private moveHighlight(direction: number): void {
    const listLength = this.useResultDropdown ? this.filteredResults.length : this.filteredSuggestions.length;
    if (!listLength) {
      return;
    }

    console.log('[DEBUG] Showing suggestions via moveHighlight (Arrow keys)');
    this.showSuggestions = true;

    const nextIndex = this.highlightedIndex + direction;
    const maxIndex = listLength - 1;

    // If no item selected and pressing down, go to first item
    if (this.highlightedIndex === -1 && direction === 1) {
      this.highlightedIndex = 0;
    } else if (this.highlightedIndex === -1 && direction === -1) {
      // If no item selected and pressing up, go to last item
      this.highlightedIndex = maxIndex;
    } else if (nextIndex < 0) {
      // Wrap to end when going past the beginning
      this.highlightedIndex = -1; // Go back to no selection
    } else if (nextIndex > maxIndex) {
      // Wrap to beginning when going past the end
      this.highlightedIndex = -1; // Go back to no selection
    } else {
      this.highlightedIndex = nextIndex;
    }

    this.ensureHighlightedVisible();
  }

  private ensureHighlightedVisible(): void {
    if (this.highlightedIndex < 0) {
      return;
    }

    requestAnimationFrame(() => {
      const buttons = this.suggestionButtons?.toArray() ?? [];
      const button = buttons[this.highlightedIndex]?.nativeElement;
      button?.scrollIntoView({ block: 'nearest' });
    });
  }

  private applyResultItems(): void {
    this.filteredResults = [...(this.resultItems ?? [])];
    
    // Only show suggestions if the input is focused
    const isFocused = this.searchInput?.nativeElement === document.activeElement;
    
    if (isFocused && this.filteredResults.length > 0) {
      this.showSuggestions = true;
      this.updateDropdownPosition();
      document.addEventListener('mousedown', this.onDocumentClick);
      console.log('[DEBUG] Showing suggestions via applyResultItems (Quick Results update) - Input is focused');
    } else {
      this.showSuggestions = false;
      if (this.filteredResults.length > 0) {
        console.log('[DEBUG] NOT showing suggestions via applyResultItems - Input is NOT focused');
      }
    }

    this.highlightedIndex = -1;
  }

  private selectResult(result: QuickResult): void {
    this.resultSelected.emit(result);
    if (!this.useResultDropdown) {
      this.hideSuggestions();
    }
  }
}
